/**
 * Cognito User Management Service
 *
 * This service provides functions to interact with AWS Cognito User Pools
 * for user management operations like disabling and deleting users.
 *
 * Required AWS IAM Permissions:
 * - cognito-idp:AdminDisableUser
 * - cognito-idp:AdminDeleteUser
 * - cognito-idp:AdminGetUser
 * - cognito-idp:AdminCreateUser (optional, for user creation)
 *
 * Required Environment Variables:
 * - REACT_APP_COGNITO_USER_POOL_ID: The Cognito User Pool ID
 * - REACT_APP_AWS_REGION: AWS Region (e.g., us-east-1)
 */

import {
  CognitoIdentityProviderClient,
  AdminDisableUserCommand,
  AdminDeleteUserCommand,
  AdminGetUserCommand,
  AdminCreateUserCommand,
} from "@aws-sdk/client-cognito-identity-provider";

// Create Cognito Identity Provider client
const cognitoClient = new CognitoIdentityProviderClient({
  region: process.env.REACT_APP_AWS_REGION || "us-east-1",
});

/**
 * Helper function to execute Cognito commands
 */
const executeCognitoCommand = async (command) => {
  try {
    const result = await cognitoClient.send(command);
    return result;
  } catch (error) {
    throw error;
  }
};

/**
 * Custom error class for Cognito service errors
 */
export class CognitoServiceError extends Error {
  constructor(message, code, originalError) {
    super(message);
    this.name = "CognitoServiceError";
    this.code = code;
    this.originalError = originalError;
  }
}

/**
 * Error codes for Cognito service operations
 */
export const ErrorCodes = {
  USER_NOT_FOUND: "USER_NOT_FOUND",
  USER_NOT_FOUND_EXCEPTION: "UserNotFoundException",
  INVALID_PARAMETER: "InvalidParameterException",
  NOT_AUTHORIZED: "NotAuthorizedException",
  TOO_MANY_REQUESTS: "TooManyRequestsException",
  NETWORK_ERROR: "NETWORK_ERROR",
  PERMISSION_DENIED: "PERMISSION_DENIED",
  USER_NOT_FOUND_IN_COGNITO: "USER_NOT_FOUND_IN_COGNITO",
};

/**
 * Get the Cognito User Pool ID from environment variables
 */
const getUserPoolId = () => {
  const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
  if (!userPoolId) {
    throw new CognitoServiceError(
      "Cognito User Pool ID not configured. Please set REACT_APP_COGNITO_USER_POOL_ID environment variable.",
      "CONFIGURATION_ERROR"
    );
  }
  return userPoolId;
};

/**
 * Handle Cognito API errors and convert them to standardized error codes
 * @param {Error} error - The original error from AWS SDK
 * @returns {CognitoServiceError} - Standardized error
 */
const handleCognitoError = (error) => {
  const errorMessage = error.message || error.code || "Unknown error occurred";

  if (
    error.code === "UserNotFoundException" ||
    error.code === "ResourceNotFoundException"
  ) {
    return new CognitoServiceError(
      `User not found in Cognito: ${errorMessage}`,
      ErrorCodes.USER_NOT_FOUND_IN_COGNITO,
      error
    );
  }

  if (error.code === "InvalidParameterException") {
    return new CognitoServiceError(
      `Invalid parameter: ${errorMessage}`,
      ErrorCodes.INVALID_PARAMETER,
      error
    );
  }

  if (error.code === "NotAuthorizedException") {
    return new CognitoServiceError(
      `Not authorized to perform this operation: ${errorMessage}`,
      ErrorCodes.NOT_AUTHORIZED,
      error
    );
  }

  if (error.code === "TooManyRequestsException") {
    return new CognitoServiceError(
      `Too many requests. Please try again later: ${errorMessage}`,
      ErrorCodes.TOO_MANY_REQUESTS,
      error
    );
  }

  if (error.code === "ConditionalCheckFailedException") {
    return new CognitoServiceError(
      `Conditional check failed: ${errorMessage}`,
      ErrorCodes.INVALID_PARAMETER,
      error
    );
  }

  // Network errors
  if (
    error.code === "NetworkingError" ||
    error.code === "ECONNREFUSED" ||
    !error.code
  ) {
    return new CognitoServiceError(
      `Network error: ${errorMessage}`,
      ErrorCodes.NETWORK_ERROR,
      error
    );
  }

  // Default case
  return new CognitoServiceError(
    `Cognito operation failed: ${errorMessage}`,
    error.code || "UNKNOWN_ERROR",
    error
  );
};

/**
 * Cognito User Service
 *
 * Provides methods for managing users in AWS Cognito User Pools.
 * All methods return promises and include proper error handling.
 */
export const cognitoUserService = {
  /**
   * Disable a user in Cognito (prevents sign-in)
   * Uses AdminDisableUser API via backend Lambda function
   *
   * @param {string} username - The username or email of the user to disable
   * @returns {Promise<void>} - Resolves when user is disabled
   * @throws {CognitoServiceError} - If operation fails
   */
  async disableUser(username) {
    if (!username || typeof username !== "string") {
      throw new CognitoServiceError(
        "Username is required and must be a string",
        ErrorCodes.INVALID_PARAMETER
      );
    }

    // Call backend API instead of using AWS SDK directly
    try {
      const { API } = await import("aws-amplify");
      const { Auth } = await import("aws-amplify");
      const session = await Auth.currentSession();
      
      const response = await API.post("createUser", "/cognito/disableUser", {
        headers: {
          "Content-Type": "application/json",
          Authorization: session.getIdToken().getJwtToken(),
        },
        body: {
          username: username,
        },
      });

      if (response.success) {
        console.log(`User ${username} has been disabled in Cognito`);
        return true;
      } else {
        throw new CognitoServiceError(
          response.message || "Failed to disable user",
          ErrorCodes.NETWORK_ERROR
        );
      }
    } catch (error) {
      // Fallback: try direct AWS SDK call (will fail in browser but provides better error)
      if (error.message?.includes("Credential is missing") || error.message?.includes("is not a function")) {
        throw new CognitoServiceError(
          "Cognito operations must be performed via backend API. Please configure a Lambda function for disableUser.",
          "BACKEND_API_REQUIRED",
          error
        );
      }
      console.error(`Error disabling user ${username}:`, error);
      throw handleCognitoError(error);
    }
  },

  /**
   * Delete a user from Cognito User Pool
   * Uses AdminDeleteUser API via backend Lambda function
   *
   * @param {string} username - The username or email of the user to delete
   * @returns {Promise<void>} - Resolves when user is deleted
   * @throws {CognitoServiceError} - If operation fails
   */
  async deleteUser(username) {
    if (!username || typeof username !== "string") {
      throw new CognitoServiceError(
        "Username is required and must be a string",
        ErrorCodes.INVALID_PARAMETER
      );
    }

    // Call backend API instead of using AWS SDK directly
    try {
      const { API } = await import("aws-amplify");
      const { Auth } = await import("aws-amplify");
      const session = await Auth.currentSession();
      
      const response = await API.post("createUser", "/cognito/deleteUser", {
        headers: {
          "Content-Type": "application/json",
          Authorization: session.getIdToken().getJwtToken(),
        },
        body: {
          username: username,
        },
      });

      if (response.success) {
        console.log(`User ${username} has been deleted from Cognito`);
        return true;
      } else {
        throw new CognitoServiceError(
          response.message || "Failed to delete user",
          ErrorCodes.NETWORK_ERROR
        );
      }
    } catch (error) {
      // Fallback: try direct AWS SDK call (will fail in browser but provides better error)
      if (error.message?.includes("Credential is missing") || error.message?.includes("is not a function")) {
        throw new CognitoServiceError(
          "Cognito operations must be performed via backend API. Please configure a Lambda function for deleteUser.",
          "BACKEND_API_REQUIRED",
          error
        );
      }
      console.error(`Error deleting user ${username} from Cognito:`, error);
      throw handleCognitoError(error);
    }
  },

  /**
   * Get user status from Cognito
   * Uses AdminGetUser API
   *
   * @param {string} username - The username or email of the user
   * @returns {Promise<Object>} - User status object
   * @throws {CognitoServiceError} - If operation fails
   */
  async getUserStatus(username) {
    if (!username || typeof username !== "string") {
      throw new CognitoServiceError(
        "Username is required and must be a string",
        ErrorCodes.INVALID_PARAMETER
      );
    }

    const userPoolId = getUserPoolId();

    try {
      const result = await cognitoClient.send(
        new AdminGetUserCommand({
          UserPoolId: userPoolId,
          Username: username,
        })
      );

      return {
        username: result.Username,
        email: result.UserAttributes?.find((attr) => attr.Name === "email")
          ?.Value,
        status: result.UserStatus, // e.g., 'ENABLED', 'DISABLED', 'UNCONFIRMED'
        enabled: result.Enabled,
        created: result.UserCreateDate,
        lastModified: result.UserLastModifiedDate,
        lastLogin: result.UserLastModifiedDate, // Cognito doesn't track last login directly
      };
    } catch (error) {
      console.error(`Error getting user status for ${username}:`, error);
      throw handleCognitoError(error);
    }
  },

  /**
   * Check if a user exists in Cognito
   *
   * @param {string} username - The username or email of the user
   * @returns {Promise<boolean>} - True if user exists, false otherwise
   */
  async userExists(username) {
    try {
      await this.getUserStatus(username);
      return true;
    } catch (error) {
      if (error.code === ErrorCodes.USER_NOT_FOUND_IN_COGNITO) {
        return false;
      }
      throw error;
    }
  },

  /**
   * Create a user in Cognito (admin creation)
   * Uses AdminCreateUser API
   *
   * @param {Object} userData - User data object
   * @param {string} userData.email - User's email
   * @param {string} [userData.username] - Username (defaults to email if not provided)
   * @param {string} [userData.password] - Temporary password
   * @param {Object} [userData.customAttributes] - Custom attributes
   * @returns {Promise<Object>} - Created user details
   * @throws {CognitoServiceError} - If operation fails
   */
  async createUser(userData) {
    const { email, username, password, customAttributes } = userData;

    if (!email) {
      throw new CognitoServiceError(
        "Email is required for user creation",
        ErrorCodes.INVALID_PARAMETER
      );
    }

    const userPoolId = getUserPoolId();

    const params = {
      UserPoolId: userPoolId,
      Username: username || email,
      UserAttributes: [
        {
          Name: "email",
          Value: email,
        },
        {
          Name: "email_verified",
          Value: "true",
        },
      ],
      MessageAction: "SUPPRESS", // Don't send welcome email
    };

    if (password) {
      params.DesiredDeliveryMediums = ["EMAIL"];
      params.TemporaryPassword = password;
    }

    if (customAttributes) {
      Object.entries(customAttributes).forEach(([key, value]) => {
        params.UserAttributes.push({
          Name: key.startsWith("custom:") ? key : `custom:${key}`,
          Value: String(value),
        });
      });
    }

    try {
      const result = await cognitoClient.send(
        new AdminCreateUserCommand({
          UserPoolId: userPoolId,
          Username: username || email,
          UserAttributes: [
            {
              Name: "email",
              Value: email,
            },
            {
              Name: "email_verified",
              Value: "true",
            },
          ],
          MessageAction: "SUPPRESS",
          ...(password && {
            DesiredDeliveryMediums: ["EMAIL"],
            TemporaryPassword: password,
          }),
          ...(customAttributes && {
            UserAttributes: Object.entries(customAttributes).map(
              ([key, value]) => ({
                Name: key.startsWith("custom:") ? key : `custom:${key}`,
                Value: String(value),
              })
            ),
          }),
        })
      );
      console.log(`User ${username || email} created in Cognito`);

      return {
        username: result.User.Username,
        email: result.User.UserAttributes?.find((attr) => attr.Name === "email")
          ?.Value,
        status: result.User.UserStatus,
      };
    } catch (error) {
      console.error("Error creating user in Cognito:", error);
      throw handleCognitoError(error);
    }
  },

  /**
   * Complete delete workflow - disable user first, then delete from Cognito
   * This is the recommended approach for complete user deletion
   *
   * @param {string} username - The username or email of the user
   * @returns {Promise<Object>} - Result object with disabled and deleted flags
   */
  async completeDelete(username) {
    const result = {
      disabled: false,
      deleted: false,
      username,
    };

    try {
      // First, disable the user to prevent sign-in during deletion
      await this.disableUser(username);
      result.disabled = true;
      console.log(`User ${username} disabled successfully`);
    } catch (error) {
      // If user doesn't exist in Cognito, continue with deletion
      if (error.code !== ErrorCodes.USER_NOT_FOUND_IN_COGNITO) {
        throw error;
      }
      console.log(`User ${username} not found in Cognito, skipping disable`);
    }

    try {
      // Then delete from Cognito
      await this.deleteUser(username);
      result.deleted = true;
      console.log(`User ${username} deleted from Cognito successfully`);
    } catch (error) {
      // If user doesn't exist in Cognito, that's okay
      if (error.code === ErrorCodes.USER_NOT_FOUND_IN_COGNITO) {
        console.log(`User ${username} not found in Cognito, skipping delete`);
      } else {
        throw error;
      }
    }

    return result;
  },
};

export default cognitoUserService;
